// apps/web/src/lib/novo-cliente-form.ts — Fase 09, Plano 05 (CAD-01, OMIE-06, OMIE-09, D-02, D-05, D-09, D-10, D-11).
//
// Reducer/helpers puros do cadastro unificado (D-02): estado do formulário completo,
// máquina de consulta de CEP resiliente a corridas (requestSequence, D-09/D-10/D-11)
// e montagem do payload de POST /api/v1/cadastros/clientes (CriarClienteCompletoDto,
// OMIE-09). Nenhuma função aqui toca rede, storage ou console — são helpers puros
// consumidos pela página (novo-cliente-page.tsx), que é quem chama apiFetch/apiMutate.
import type { ConsultaCepResultado, CriarClienteCompletoDto } from '@pieltec/contracts';

export type CepLookupStatus = 'idle' | 'loading' | 'success' | 'not-found' | 'unavailable';

export interface CepLookupState {
  readonly status: CepLookupStatus;
  /** CEP normalizado (só dígitos) da última consulta iniciada. */
  readonly cep: string;
  /** Incrementado a cada nova consulta; correlaciona resposta assíncrona com a requisição que a originou (D-09). */
  readonly requestSequence: number;
}

export interface ContatoFiscalFormState {
  contato: string;
  telefone1Ddd: string;
  telefone1Numero: string;
  telefone2Ddd: string;
  telefone2Numero: string;
  email: string;
}

export interface EnderecoFiscalFormState {
  endereco: string;
  enderecoNumero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

export interface DadosFiscaisComplementaresFormState {
  inscricaoEstadual: string;
  inscricaoMunicipal: string;
  optanteSimplesNacional: boolean | null;
  observacao: string;
}

export interface NovoClienteFormState {
  codigo: string;
  nome: string;
  nomeFantasia: string;
  documentoFiscal: string;
  tipoPessoa: 'PF' | 'PJ' | '';
  contatoFiscal: ContatoFiscalFormState;
  enderecoFiscal: EnderecoFiscalFormState;
  dadosFiscaisComplementares: DadosFiscaisComplementaresFormState;
  observacoes: string;
  /** D-05: liga por padrão; usuário pode desligar antes de salvar. */
  sincronizarComOmie: boolean;
  cepLookup: CepLookupState;
}

/** D-05: estado inicial nasce com o toggle Omie ligado e nenhum campo preenchido. */
export function estadoInicialNovoCliente(): NovoClienteFormState {
  return {
    codigo: '',
    nome: '',
    nomeFantasia: '',
    documentoFiscal: '',
    tipoPessoa: '',
    contatoFiscal: {
      contato: '',
      telefone1Ddd: '',
      telefone1Numero: '',
      telefone2Ddd: '',
      telefone2Numero: '',
      email: '',
    },
    enderecoFiscal: {
      endereco: '',
      enderecoNumero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
    },
    dadosFiscaisComplementares: {
      inscricaoEstadual: '',
      inscricaoMunicipal: '',
      optanteSimplesNacional: null,
      observacao: '',
    },
    observacoes: '',
    sincronizarComOmie: true,
    cepLookup: {
      status: 'idle',
      cep: '',
      requestSequence: 0,
    },
  };
}

/** Remove tudo que não for dígito — mesma normalização usada pelo backend (D-09). */
export function normalizarCep(valorBruto: string): string {
  return valorBruto.replace(/\D/g, '');
}

/**
 * Elegibilidade única para disparo automático (8 dígitos completos) e para o botão
 * "Buscar novamente" (D-09) — os dois caminhos SEMPRE usam este mesmo predicado.
 */
export function podeConsultarCep(cepNormalizado: string): boolean {
  return /^\d{8}$/.test(cepNormalizado);
}

/**
 * Inicia uma nova consulta: incrementa requestSequence (correlação com a resposta
 * assíncrona) e marca o status como loading. O chamador deve capturar o
 * requestSequence resultante e repassá-lo para aplicarResultadoCep quando a
 * resposta chegar — respostas de requisições supersedidas são descartadas (D-09).
 */
export function iniciarConsultaCep(state: NovoClienteFormState, cepNormalizado: string): NovoClienteFormState {
  return {
    ...state,
    cepLookup: {
      status: 'loading',
      cep: cepNormalizado,
      requestSequence: state.cepLookup.requestSequence + 1,
    },
  };
}

/**
 * Aplica o resultado de uma consulta de CEP (D-10, D-11):
 * - ENCONTRADO substitui SOMENTE endereco/bairro/cidade/estado; numero/complemento
 *   e todos os demais campos permanecem intocados e editáveis.
 * - NAO_ENCONTRADO/INDISPONIVEL/INVALIDO nunca alteram campos — só o status do
 *   aviso contextual muda; o formulário NUNCA fica bloqueado (não existe
 *   `formDisabled` neste estado).
 * - Se requestSequence não bate mais com o requestSequence atual do estado
 *   (usuário já editou o CEP de novo e iniciou outra consulta), a resposta é
 *   descartada sem qualquer mutação — guarda contra corrida (D-09).
 */
export function aplicarResultadoCep(
  state: NovoClienteFormState,
  resultado: ConsultaCepResultado,
  requestSequence: number,
): NovoClienteFormState {
  if (requestSequence !== state.cepLookup.requestSequence) {
    return state;
  }

  switch (resultado.status) {
    case 'ENCONTRADO':
      return {
        ...state,
        enderecoFiscal: {
          ...state.enderecoFiscal,
          endereco: resultado.endereco,
          bairro: resultado.bairro,
          cidade: resultado.cidade,
          estado: resultado.uf,
        },
        cepLookup: { ...state.cepLookup, status: 'success' },
      };
    case 'NAO_ENCONTRADO':
      return { ...state, cepLookup: { ...state.cepLookup, status: 'not-found' } };
    case 'INDISPONIVEL':
    case 'INVALIDO':
    default:
      return { ...state, cepLookup: { ...state.cepLookup, status: 'unavailable' } };
  }
}

function semEspacos(valor: string): string | undefined {
  const aparado = valor.trim();
  return aparado.length > 0 ? aparado : undefined;
}

/** Monta o DTO de POST /api/v1/cadastros/clientes a partir do estado (OMIE-09, D-05). */
export function montarPayloadCriarCliente(state: NovoClienteFormState): CriarClienteCompletoDto {
  const contatoFiscal = {
    contato: semEspacos(state.contatoFiscal.contato),
    telefone1Ddd: semEspacos(state.contatoFiscal.telefone1Ddd),
    telefone1Numero: semEspacos(state.contatoFiscal.telefone1Numero),
    telefone2Ddd: semEspacos(state.contatoFiscal.telefone2Ddd),
    telefone2Numero: semEspacos(state.contatoFiscal.telefone2Numero),
    email: semEspacos(state.contatoFiscal.email),
  };
  const contatoFiscalPreenchido = Object.values(contatoFiscal).some((v) => v !== undefined);

  const enderecoFiscal = {
    endereco: semEspacos(state.enderecoFiscal.endereco),
    enderecoNumero: semEspacos(state.enderecoFiscal.enderecoNumero),
    complemento: semEspacos(state.enderecoFiscal.complemento),
    bairro: semEspacos(state.enderecoFiscal.bairro),
    cidade: semEspacos(state.enderecoFiscal.cidade),
    estado: semEspacos(state.enderecoFiscal.estado),
    cep: state.enderecoFiscal.cep.trim().length > 0 ? normalizarCep(state.enderecoFiscal.cep) : undefined,
  };
  const enderecoFiscalPreenchido = Object.values(enderecoFiscal).some((v) => v !== undefined);

  const dadosFiscaisComplementares = {
    inscricaoEstadual: semEspacos(state.dadosFiscaisComplementares.inscricaoEstadual),
    inscricaoMunicipal: semEspacos(state.dadosFiscaisComplementares.inscricaoMunicipal),
    optanteSimplesNacional:
      state.dadosFiscaisComplementares.optanteSimplesNacional === null
        ? undefined
        : state.dadosFiscaisComplementares.optanteSimplesNacional,
    observacao: semEspacos(state.dadosFiscaisComplementares.observacao),
  };
  const dadosFiscaisComplementaresPreenchido = Object.values(dadosFiscaisComplementares).some(
    (v) => v !== undefined,
  );

  return {
    codigo: state.codigo.trim(),
    nome: state.nome.trim(),
    nomeFantasia: semEspacos(state.nomeFantasia),
    documentoFiscal: state.documentoFiscal.trim(),
    tipoPessoa: state.tipoPessoa === '' ? undefined : state.tipoPessoa,
    contatoFiscal: contatoFiscalPreenchido ? contatoFiscal : undefined,
    enderecoFiscal: enderecoFiscalPreenchido ? enderecoFiscal : undefined,
    dadosFiscaisComplementares: dadosFiscaisComplementaresPreenchido ? dadosFiscaisComplementares : undefined,
    observacoes: semEspacos(state.observacoes),
    // D-05: booleano explícito do usuário sempre respeitado (inclusive false).
    sincronizarComOmie: state.sincronizarComOmie,
  };
}
